import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CButton, CCard, CCardBody } from "@coreui/react-pro";
import { useMyMemberships } from "../../../api/hooks/useMemberships";
import { useMyMembershipRequests } from "../../../api/hooks/useMembershipRequests";
import { isExternalTier, useMembershipTiers } from "../../../api/hooks/useMembershipTiers";
import { routePaths } from "../../../routes/route-paths";

const DISMISSED_KEY = "scryer-membership-prompt-dismissed";

const readDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    // Private windows and blocked site data throw on access. Showing the prompt again is harmless.
    return false;
  }
};

/** "Already a member on Patreon or X?" for someone with no membership and nothing asked about yet. */
export function MembershipPrompt() {
  const { t } = useTranslation("memberships");
  const tiers = useMembershipTiers();
  const memberships = useMyMemberships();
  const requests = useMyMembershipRequests();
  const [dismissed, setDismissed] = useState(readDismissed);

  const platformNames = [
    ...new Set((tiers.data ?? []).filter(isExternalTier).map((tier) => tier.platform.name)),
  ];
  // Anyone holding a membership, or who has already told us about one, knows where to find this.
  const knowsAlready = (memberships.data?.length ?? 1) > 0 || (requests.data?.length ?? 1) > 0;

  if (dismissed || knowsAlready || platformNames.length === 0) {
    return null;
  }

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // Nothing to do - it shows again next time the dashboard opens.
    }
    setDismissed(true);
  };

  const platforms = new Intl.ListFormat("en", { style: "long", type: "disjunction" }).format(platformNames);

  return (
    <CCard className="mb-4">
      <CCardBody className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h2 className="h5 mb-1">{t("prompt.title", { platforms })}</h2>
          <p className="text-body-secondary mb-0">{t("prompt.body")}</p>
        </div>
        <div className="d-flex gap-2">
          <CButton color="secondary" variant="outline" onClick={dismiss}>
            {t("prompt.notMe")}
          </CButton>
          <Link to={routePaths.memberships} className="btn btn-primary">
            {t("prompt.tellUs")}
          </Link>
        </div>
      </CCardBody>
    </CCard>
  );
}
